import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';

const RegisterPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { register } = useAuth();
  const { toast } = useToast();
  
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const redirect = searchParams.get('redirect') || '/dashboard';

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      toast({
        title: "Erreur",
        description: "Les mots de passe ne correspondent pas",
        variant: "destructive",
      });
      return;
    }

    if (formData.password.length < 6) {
      toast({
        title: "Erreur",
        description: "Le mot de passe doit contenir au moins 6 caractères",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);

    try {
      await register({
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        phone: formData.phone || null,
        password: formData.password,
      });
      toast({
        title: "Compte créé",
        description: "Bienvenue sur Mon Séjour en Bretagne !",
      });
      navigate(redirect);
    } catch (error) {
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Une erreur est survenue",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-20 flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-lg border border-[#E2E8F0] p-8">
          <div className="text-center mb-8">
            <h1 className="font-['Outfit'] text-2xl font-medium text-[#0F172A] mb-2">
              Créer un compte
            </h1>
            <p className="text-[#475569]">
              Rejoignez Mon Séjour en Bretagne
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="register-form">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="first_name" className="text-[#0F172A]">Prénom</Label>
                <Input
                  id="first_name"
                  name="first_name"
                  type="text"
                  value={formData.first_name}
                  onChange={handleChange}
                  placeholder="Jean"
                  className="mt-1.5 h-12 rounded-xl border-[#E2E8F0]"
                  required
                  data-testid="register-firstname"
                />
              </div>
              <div>
                <Label htmlFor="last_name" className="text-[#0F172A]">Nom</Label>
                <Input
                  id="last_name"
                  name="last_name"
                  type="text"
                  value={formData.last_name}
                  onChange={handleChange}
                  placeholder="Dupont"
                  className="mt-1.5 h-12 rounded-xl border-[#E2E8F0]"
                  required
                  data-testid="register-lastname"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="email" className="text-[#0F172A]">Email</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="votre@email.com"
                className="mt-1.5 h-12 rounded-xl border-[#E2E8F0]"
                required
                data-testid="register-email"
              />
            </div>

            <div>
              <Label htmlFor="phone" className="text-[#0F172A]">Téléphone (optionnel)</Label>
              <Input
                id="phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                placeholder="06 00 00 00 00"
                className="mt-1.5 h-12 rounded-xl border-[#E2E8F0]"
                data-testid="register-phone"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-[#0F172A]">Mot de passe</Label>
              <div className="relative mt-1.5">
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="••••••••"
                  className="h-12 rounded-xl border-[#E2E8F0] pr-10"
                  required
                  data-testid="register-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#475569]"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            <div>
              <Label htmlFor="confirmPassword" className="text-[#0F172A]">Confirmer le mot de passe</Label>
              <Input
                id="confirmPassword"
                name="confirmPassword"
                type={showPassword ? 'text' : 'password'}
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="••••••••"
                className="mt-1.5 h-12 rounded-xl border-[#E2E8F0]"
                required
                data-testid="register-confirm-password"
              />
            </div>

            <Button
              type="submit"
              className="w-full h-12 bg-[#2D3E50] hover:bg-[#3D4F61] rounded-lg text-base"
              disabled={loading}
              data-testid="register-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Création...
                </>
              ) : (
                'Créer mon compte'
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-[#475569]">
              Déjà un compte ?{' '}
              <Link 
                to={`/login${redirect !== '/dashboard' ? `?redirect=${redirect}` : ''}`}
                className="text-[#E8945A] font-medium hover:underline"
                data-testid="login-link"
              >
                Se connecter
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
